import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { transactionApi, accountApi, clientApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Account } from '@/types'
import { Plus, Trash2, CheckCircle, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

interface JournalLine {
  account_id: string
  description: string
  debit: string
  credit: string
}

const EMPTY_LINE: JournalLine = { account_id: '', description: '', debit: '', credit: '' }

export default function JournalEntry() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)

  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const [currency, setCurrency] = useState('USD')

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    narration: '',
    is_reversing: false,
    reverse_date: '',
  })

  const [lines, setLines] = useState<JournalLine[]>([
    { ...EMPTY_LINE },
    { ...EMPTY_LINE },
  ])

  useEffect(() => {
    if (!clientId) return
    clientApi.get(clientId).then((res) => {
      setCurrentClient(res.data.client)
      setCurrency(res.data.client.currency)
    })
    setLoadingAccounts(true)
    accountApi
      .list(clientId)
      .then((res) => setAccounts(res.data.accounts))
      .finally(() => setLoadingAccounts(false))
  }, [clientId, setCurrentClient])

  // --- Computed values ---
  const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0)
  const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0)
  const difference = Math.abs(totalDebit - totalCredit)
  const isBalanced = totalDebit === totalCredit && totalDebit > 0
  const hasEntries = totalDebit > 0 || totalCredit > 0

  // --- Group accounts by type for the dropdown ---
  const accountsByType = accounts
    .filter((a) => !a.is_archived)
    .reduce<Record<string, Account[]>>((groups, account) => {
      const type = account.account_type
      if (!groups[type]) groups[type] = []
      groups[type].push(account)
      return groups
    }, {})

  const typeLabels: Record<string, string> = {
    asset: 'Assets',
    liability: 'Liabilities',
    equity: 'Equity',
    revenue: 'Revenue',
    expense: 'Expenses',
  }
  const typeOrder = ['asset', 'liability', 'equity', 'revenue', 'expense']

  // --- Line manipulation ---
  function updateLine(index: number, field: keyof JournalLine, value: string) {
    setLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_LINE }])
  }

  function removeLine(index: number) {
    if (lines.length <= 2) return
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  // --- Submit ---
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!isBalanced) {
      toast.error('Journal must be balanced — total debits must equal total credits.')
      return
    }

    const validLines = lines.filter((l) => l.account_id)
    if (validLines.length < 2) {
      toast.error('At least two lines with accounts are required.')
      return
    }

    setLoading(true)
    try {
      await transactionApi.createJournal({
        client_ledger_id: clientId!,
        date: form.date,
        narration: form.narration,
        is_reversing: form.is_reversing,
        reverse_date: form.is_reversing && form.reverse_date ? form.reverse_date : undefined,
        lines: validLines.map((l) => ({
          account_id: l.account_id,
          description: l.description,
          debit: l.debit || '0',
          credit: l.credit || '0',
        })),
      })
      toast.success('Journal entry posted successfully')
      navigate(`/clients/${clientId}/transactions`)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to post journal entry'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  if (loadingAccounts) {
    return (
      <div className="flex items-center justify-center py-20 text-[15px] text-[rgba(0,10,30,0.75)]">
        Loading accounts...
      </div>
    )
  }

  return (
    <div className="max-w-5xl">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">New Journal Entry</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">
            Record a manual double-entry journal voucher
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/clients/${clientId}/transactions`)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="create"
            onClick={handleSubmit as unknown as () => void}
            disabled={loading || !isBalanced}
          >
            {loading ? 'Posting...' : 'Post Journal'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Header fields card */}
        <Card className="mb-4">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Narration</Label>
                <Input
                  type="text"
                  value={form.narration}
                  onChange={(e) => setForm({ ...form, narration: e.target.value })}
                  placeholder="Description of this journal entry"
                />
              </div>
            </div>

            {/* Reversing entry */}
            <div className="mt-4 flex items-center gap-4">
              <label className="flex items-center gap-2 text-[15px] text-[#000A1E] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.is_reversing}
                  onChange={(e) =>
                    setForm({ ...form, is_reversing: e.target.checked, reverse_date: '' })
                  }
                  className="rounded border-[#CCCED2] text-[#0078C8] focus:ring-[#0078C8]/20"
                />
                Reversing entry
              </label>
              {form.is_reversing && (
                <div className="flex items-center gap-2">
                  <Label>Reverse on</Label>
                  <Input
                    type="date"
                    value={form.reverse_date}
                    onChange={(e) => setForm({ ...form, reverse_date: e.target.value })}
                    className="w-auto"
                    min={form.date}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Journal lines table */}
        <Card className="overflow-hidden mb-4">
          <CardHeader className="px-6 py-3 border-b border-border">
            <h2 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">
              Journal Lines
            </h2>
          </CardHeader>

          <div className="overflow-x-auto">
            <table className="w-full text-[15px]">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left text-[13px] font-bold text-[#000A1E]" style={{ minWidth: 220 }}>
                    Account
                  </th>
                  <th className="px-4 py-3 text-left text-[13px] font-bold text-[#000A1E]" style={{ minWidth: 180 }}>
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-[13px] font-bold text-[#000A1E] w-36">Debit</th>
                  <th className="px-4 py-3 text-right text-[13px] font-bold text-[#000A1E] w-36">Credit</th>
                  <th className="w-12"></th>
                </tr>
              </thead>

              <tbody>
                {lines.map((line, i) => {
                  const hasDebit = !!line.debit && parseFloat(line.debit) > 0
                  const hasCredit = !!line.credit && parseFloat(line.credit) > 0

                  return (
                    <tr
                      key={i}
                      className="border-b border-border hover:bg-muted/20 group"
                    >
                      {/* Account */}
                      <td className="px-4 py-2">
                        <select
                          value={line.account_id}
                          onChange={(e) => updateLine(i, 'account_id', e.target.value)}
                          className="input"
                        >
                          <option value="">Select account...</option>
                          {typeOrder.map((type) => {
                            const group = accountsByType[type]
                            if (!group || group.length === 0) return null
                            return (
                              <optgroup key={type} label={typeLabels[type] || type}>
                                {group.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.code} — {a.name}
                                  </option>
                                ))}
                              </optgroup>
                            )
                          })}
                        </select>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-2">
                        <Input
                          type="text"
                          value={line.description}
                          onChange={(e) => updateLine(i, 'description', e.target.value)}
                          placeholder="Line memo"
                        />
                      </td>

                      {/* Debit */}
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.debit}
                          onChange={(e) => updateLine(i, 'debit', e.target.value)}
                          className="text-right font-mono tabular-nums"
                          placeholder="0.00"
                          disabled={hasCredit}
                        />
                      </td>

                      {/* Credit */}
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.credit}
                          onChange={(e) => updateLine(i, 'credit', e.target.value)}
                          className="text-right font-mono tabular-nums"
                          placeholder="0.00"
                          disabled={hasDebit}
                        />
                      </td>

                      {/* Remove button */}
                      <td className="px-2 py-2 text-center">
                        {lines.length > 2 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => removeLine(i)}
                            className="text-[#CCCED2] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove line"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <div className="w-7" />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>

              {/* Footer: Add line + totals */}
              <tfoot>
                <tr className="border-t border-border bg-muted/20">
                  <td colSpan={2} className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addLine}
                      className="text-[#0078C8]"
                    >
                      <Plus className="w-4 h-4" />
                      Add line
                    </Button>
                  </td>
                  <td className="px-4 py-3 text-right font-bold font-mono tabular-nums text-[#000A1E]">
                    {formatCurrency(totalDebit, currency)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold font-mono tabular-nums text-[#000A1E]">
                    {formatCurrency(totalCredit, currency)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        {/* Balance indicator */}
        <div className="flex items-center justify-between">
          <div>
            {hasEntries && (
              <div className="flex items-center gap-2">
                {isBalanced ? (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Balanced
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Out of balance by {formatCurrency(difference, currency)}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Bottom action buttons (duplicated for convenience on long forms) */}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/clients/${clientId}/transactions`)}
            >
              Cancel
            </Button>
            <Button type="submit" variant="create" disabled={loading || !isBalanced}>
              {loading ? 'Posting...' : 'Post Journal'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
